import { Check, ChevronDown, Loader2, Plus, Search, X } from "lucide-react";
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFloatingMenuPosition } from "@/hooks/useFloatingMenuPosition";

/** Matches this component's previous hardcoded dropdown max height. */
const DROPDOWN_MAX_HEIGHT = 240;

interface FetchSearchableDropdownOption {
  value: string;
  label: string;
  /** Compact text shown on the trigger when this option is selected. Falls back to `label`.
   *  Use when the full `label` (kept for the searchable list) is too wide for the trigger. */
  triggerLabel?: string;
  icon?: React.ReactNode;
  /** Called when this option is selected, receiving the current search term. Only used for pinned options. */
  onSelect?: (searchTerm: string) => void;
}

interface FetchSearchableDropdownProps {
  id?: string;
  /** Accessible name for the combobox trigger — a `div[role="combobox"]` isn't a natively
   * labelable element, so an external `<label htmlFor>` doesn't associate with it; use this
   * instead when the trigger needs an accessible name beyond its visible selected value. */
  ariaLabel?: string;
  options: FetchSearchableDropdownOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  status?: "default" | "error";
  errorMessage?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  allowCreate?: boolean;
  noOptionsMessage?: React.ReactNode;
  createLabel?: (searchTerm: string) => string;
  onCreateOption?: (searchTerm: string) => void;
  onSearchChange?: (searchTerm: string) => void;
  loading?: boolean;
  helperText?: string;
  /** Minimum width (px) for the open menu. Lets a compact trigger show a wider list
   *  so long option labels aren't truncated. The menu is clamped to stay on-screen. */
  menuMinWidth?: number;
  /** Optional container element for the portal. Useful when inside modals/dialogs. */
  portalContainer?: HTMLElement | null;
  /** Options that are always shown at the top and not filtered or counted in results */
  pinnedOptions?: FetchSearchableDropdownOption[];
}

/**
 * Nowadays branded searchable dropdown component with filtering capabilities.
 * Allows users to search through options and optionally create new ones.
 */
export const FetchSearchableDropdown = ({
  id,
  ariaLabel,
  options = [],
  value,
  onChange,
  placeholder = "Type to search...",
  status = "default",
  errorMessage,
  clearable = false,
  disabled = false,
  className = "",
  allowCreate = false,
  noOptionsMessage = "No options found",
  createLabel = (term) => `+ Create "${term}"`,
  onCreateOption,
  onSearchChange,
  loading = false,
  helperText,
  menuMinWidth,
  portalContainer,
  pinnedOptions = [],
}: FetchSearchableDropdownProps) => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { position: dropdownPosition } = useFloatingMenuPosition(containerRef, {
    isOpen,
    maxHeight: DROPDOWN_MAX_HEIGHT,
    minWidth: menuMinWidth,
  });

  // Find the selected option (check both regular and pinned options)
  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) || pinnedOptions.find((opt) => opt.value === value),
    [options, pinnedOptions, value]
  );

  // Filter options based on search term
  // If onSearchChange is provided, disable client-side filtering (server-side filtering)
  const filteredOptions = useMemo(() => {
    if (onSearchChange) return options;
    if (!searchTerm) return options;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(lowerSearchTerm));
  }, [options, searchTerm, onSearchChange]);

  // Check if we should show the create option
  // Always show when allowCreate is true, but hide if there's an exact match
  const showCreateOption = useMemo(() => {
    if (!allowCreate) return false;

    // Don't show create option if exact match exists
    if (searchTerm) {
      const exactMatch = options.some((opt) => opt.label.toLowerCase() === searchTerm.toLowerCase());
      if (exactMatch) return false;
    }

    return true;
  }, [allowCreate, searchTerm, options]);

  // Total options including pinned and create option
  const totalOptions = pinnedOptions.length + filteredOptions.length + (showCreateOption ? 1 : 0);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(0);
      }
    };

    if (isOpen) {
      document.addEventListener("pointerdown", handleClickOutside, true);
      return () => document.removeEventListener("pointerdown", handleClickOutside, true);
    }
  }, [isOpen]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handlers
  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm("");
        setHighlightedIndex(0);
      }
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
    setHighlightedIndex(0);
  };

  const handlePinnedSelect = (option: FetchSearchableDropdownOption) => {
    if (option.onSelect) {
      option.onSelect(searchTerm);
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(0);
    } else {
      handleSelect(option.value);
    }
  };

  const handleCreate = () => {
    if (showCreateOption) {
      if (onCreateOption) {
        onCreateOption(searchTerm);
      }
      // Use "create-new" as the value when creating a new option
      // This matches the pattern used in EventInformation
      handleSelect("create-new");
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearchTerm("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % totalOptions);
        break;

      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + totalOptions) % totalOptions);
        break;

      case "Enter":
        e.preventDefault();
        // Create option is at index 0 when showCreateOption is true
        if (showCreateOption && highlightedIndex === 0) {
          handleCreate();
        } else {
          const createOffset = showCreateOption ? 1 : 0;
          const pinnedIndex = highlightedIndex - createOffset;

          if (pinnedIndex < pinnedOptions.length) {
            handlePinnedSelect(pinnedOptions[pinnedIndex]);
          } else {
            const optionIndex = pinnedIndex - pinnedOptions.length;
            if (filteredOptions[optionIndex]) {
              handleSelect(filteredOptions[optionIndex].value);
            }
          }
        }
        break;

      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(0);
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Styles
  const baseClasses =
    "font-medium text-sm w-full min-w-full px-3 py-2 rounded-lg border focus:ring-2 focus:outline-none transition-all duration-200 bg-white appearance-none cursor-pointer touch-manipulation";

  const stateClasses = {
    default:
      "border-gray-200 hover:border-gray-300 focus:border-indigo-400 focus:ring-indigo-200 focus:shadow-[0_0_0_3px_rgba(165,180,252,0.2)]",
    error:
      "border-red-400 ring-2 ring-red-200 shadow-[0_0_0_3px_rgba(252,165,165,0.2)] hover:border-red-500 focus:border-red-500 focus:ring-red-200 focus:shadow-[0_0_0_3px_rgba(252,165,165,0.2)]",
  };

  const disabledClasses = disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : "";

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Main trigger button */}
      <div
        id={id}
        aria-label={ariaLabel}
        className={`${baseClasses} ${stateClasses[status]} ${disabledClasses} ${className}
          flex items-center justify-between min-w-full ${!selectedOption && !searchTerm ? "text-gray-400" : "text-gray-700"}`}
        onClick={handleToggle}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={disabled}
      >
        <span className={`flex items-center gap-2 truncate ${isOpen && searchTerm ? "invisible" : ""}`}>
          {selectedOption?.icon && <span className="flex-shrink-0">{selectedOption.icon}</span>}
          {selectedOption?.triggerLabel ?? selectedOption?.label ?? placeholder}
        </span>

        {/* Search input (hidden when closed) */}
        {isOpen && (
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => {
              const newSearchTerm = e.target.value;
              setSearchTerm(newSearchTerm);
              if (onSearchChange) {
                onSearchChange(newSearchTerm);
              }
            }}
            onKeyDown={handleKeyDown}
            className="absolute inset-0 w-full px-3 py-2 pr-14 bg-transparent outline-none"
            onClick={(e) => e.stopPropagation()}
          />
        )}

        <div className={`flex items-center gap-1 ${isOpen ? "relative z-10" : ""}`}>
          {/* Search icon or loading indicator (when open) */}
          {isOpen && (
            <div className="pr-1">
              {loading ? (
                <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
              ) : (
                <Search className="w-4 h-4 text-gray-400" />
              )}
            </div>
          )}

          {/* Clear button */}
          {clearable && value && !isOpen && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 hover:bg-gray-100 rounded transition-colors"
              aria-label="Clear selection"
            >
              <X className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}

          {/* Dropdown chevron */}
          <ChevronDown
            className={`w-4 h-4 text-gray-600 transition-transform duration-200 
              ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {/* Dropdown menu - rendered via portal */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto"
            style={{
              ...(dropdownPosition.opensUpward
                ? { bottom: `${window.innerHeight - dropdownPosition.top}px` }
                : { top: `${dropdownPosition.top}px` }),
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              zIndex: 100000,
              maxHeight: 240,
            }}
            role="listbox"
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2 flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium">
                {filteredOptions.length} {filteredOptions.length === 1 ? "result" : "results"}
              </span>
            </div>

            {/* Create new option - shown at top when allowCreate is true */}
            {showCreateOption && (
              <div className="sticky top-[33px] z-10 bg-white border-b border-gray-100">
                <button
                  type="button"
                  data-index={0}
                  className={`flex w-full touch-manipulation items-center gap-2 px-3 py-2 text-left text-sm font-medium
                    ${highlightedIndex === 0 ? "bg-indigo-50 text-indigo-700" : "hover:bg-gray-50 text-indigo-600"}`}
                  onClick={handleCreate}
                  onMouseEnter={() => setHighlightedIndex(0)}
                  role="option"
                >
                  <Plus className="w-4 h-4" />
                  <span>{createLabel(searchTerm)}</span>
                </button>
              </div>
            )}

            {/* Pinned options (always shown, not counted in results) */}
            {pinnedOptions.length > 0 && (
              <div
                className={`sticky ${showCreateOption ? "top-[73px]" : "top-[33px]"} z-10 bg-white border-b border-gray-100`}
              >
                {pinnedOptions.map((option, index) => {
                  const adjustedIndex = (showCreateOption ? 1 : 0) + index;
                  return (
                    <button
                      type="button"
                      key={option.value}
                      data-index={adjustedIndex}
                      className={`flex w-full touch-manipulation items-center gap-2 px-3 py-2 text-left text-sm font-medium
                        ${highlightedIndex === adjustedIndex ? "bg-indigo-50 text-indigo-700" : "hover:bg-gray-50 text-indigo-600"}`}
                      onClick={() => handlePinnedSelect(option)}
                      onMouseEnter={() => setHighlightedIndex(adjustedIndex)}
                      role="option"
                      aria-selected={option.value === value}
                    >
                      {option.icon || <Plus className="w-4 h-4" />}
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Options list */}
            <div className="py-1">
              {filteredOptions.length === 0 && !showCreateOption ? (
                <div className="px-3 py-2 text-sm text-gray-500 font-medium">{noOptionsMessage}</div>
              ) : (
                <>
                  {/* Regular options */}
                  {filteredOptions.map((option, index) => {
                    const adjustedIndex = (showCreateOption ? 1 : 0) + pinnedOptions.length + index;
                    return (
                      <button
                        type="button"
                        key={option.value}
                        data-index={adjustedIndex}
                        className={`flex w-full touch-manipulation items-center gap-2 px-3 py-2 text-left text-sm font-medium
                          ${highlightedIndex === adjustedIndex ? "bg-indigo-50 text-indigo-700" : "hover:bg-gray-50"}`}
                        onClick={() => handleSelect(option.value)}
                        onMouseEnter={() => setHighlightedIndex(adjustedIndex)}
                        role="option"
                        aria-selected={option.value === value}
                      >
                        {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                        <span className="truncate">{option.label}</span>
                        {option.value === value && <Check className="w-4 h-4 text-indigo-600 flex-shrink-0 ml-auto" />}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>,
          portalContainer || document.body
        )}

      {/* Error message */}
      {status === "error" && errorMessage && <p className="mt-1 text-sm text-red-500 font-medium">{errorMessage}</p>}

      {/* Helper text */}
      {helperText && !errorMessage && <p className="mt-1 text-xs text-gray-600">{helperText}</p>}
    </div>
  );
};

FetchSearchableDropdown.displayName = "FetchSearchableDropdown";
